import type {
  FeatureUpdate,
  GamblePick,
  LineSlotPlay,
  RoundDetail,
  SpinResult,
  Stake,
} from '@sirius/contracts/admin'
import {
  decodeExpandingSymbolData,
  decodeMultiplierFeatureData,
  FEATURE_KIND_EXPANDING,
  FEATURE_KIND_MULTIPLIER,
} from '@sirius/contracts/admin'
import type { TFunction } from 'i18next'
import { formatCredits, formatNumber } from '~/i18n/format'

/** A round's page, under its cabinet (the `rounds` resource's show route). */
export function roundPath({
  cabinetId,
  id,
}: {
  cabinetId: string
  id: string
}) {
  return `/cabinets/${cabinetId}/rounds/${id}`
}

/** A spin of a round: the paid spin or one of its free games. */
export type SpinPlay = Extract<LineSlotPlay, { action: 'spin' }>

/** A step on the round's gamble ladder. */
export type GambleStep =
  /** the pending win was put at risk */
  | { kind: 'opened'; total: number }
  /** a pick; `total` is the ladder after it, null after a lost pick */
  | { kind: 'pick'; attempt: number; pick: GamblePick; total: number | null }

/** A round's plays (`outcome`), by what the detail view shows of them. */
export interface RoundSteps {
  paidSpin: SpinPlay | undefined
  /** in the order they were played */
  freeGames: SpinPlay[]
  gamble: GambleStep[]
  /** what the collect paid out (the win pending before it); null without one */
  collected: number | null
}

export function roundSteps(outcome: readonly LineSlotPlay[]): RoundSteps {
  const steps: RoundSteps = {
    paidSpin: undefined,
    freeGames: [],
    gamble: [],
    collected: null,
  }
  let attempt = 0
  let previous: LineSlotPlay | undefined
  for (const play of outcome) {
    switch (play.action) {
      case 'spin':
        if (play.spinKind === 'paid') {
          steps.paidSpin = play
        } else {
          steps.freeGames.push(play)
        }
        break
      case 'gamble':
        steps.gamble.push({ kind: 'opened', total: play.gamble.total })
        break
      case 'pick':
        attempt += 1
        steps.gamble.push({
          kind: 'pick',
          attempt,
          pick: play.pick,
          total: play.gamble?.total ?? null,
        })
        break
      case 'collect':
        steps.collected = winPendingAfter(previous)
        break
    }
    previous = play
  }
  return steps
}

/**
 * What is won but not paid out yet after `play`: a spin's win, the feature's
 * win so far (a trigger's own win is paid out at once), the gamble ladder's
 * total; nothing after a collect, a lost pick or before the first play.
 */
function winPendingAfter(play: LineSlotPlay | undefined): number {
  switch (play?.action) {
    case undefined:
    case 'collect':
      return 0
    case 'spin':
      return play.feature === null
        ? play.result.totalWin
        : play.feature.state.featureWin
    case 'gamble':
      return play.gamble.total
    case 'pick':
      return play.gamble?.total ?? 0
  }
}

/**
 * What an open round has won but not paid out yet, from its last play (see
 * `winPendingAfter`). Null for a closed round: everything it won is in
 * `totalWin`.
 */
export function pendingWin(
  round: Pick<RoundDetail, 'closedAt' | 'outcome'>,
): number | null {
  return round.closedAt === null ? winPendingAfter(round.outcome.at(-1)) : null
}

/** The key of a grid cell in `winningCells`. */
export function cellKey(reel: number, row: number) {
  return `${reel}:${row}`
}

/**
 * The grid cells that took part in a spin's wins: each winning payline's
 * first `matchCount` cells (Book of Ra pays from the left), every scatter of
 * a scatter win and each reel an expanding symbol fills. A payline the
 * config lacks is skipped.
 */
export function winningCells(
  result: SpinResult,
  paylines: readonly (readonly number[])[],
): Set<string> {
  const cells = new Set<string>()
  for (const win of result.paylineWins) {
    const payline = paylines.at(win.paylineIndex)
    if (payline === undefined) {
      continue
    }
    payline.slice(0, win.matchCount).forEach((row, reel) => {
      cells.add(cellKey(reel, row))
    })
  }
  const scatters = new Set(result.scatterWins.map((win) => win.symbolId))
  result.grid.forEach((reel, reelIndex) => {
    reel.forEach((symbol, row) => {
      if (
        scatters.has(symbol) ||
        result.expandingWin?.reels.includes(reelIndex) === true
      ) {
        cells.add(cellKey(reelIndex, row))
      }
    })
  })
  return cells
}

/** `grid[reel][row]` as rows, top to bottom: `rows[row][reel]`. */
export function gridRows(grid: readonly (readonly string[])[]): string[][] {
  const rowCount = Math.max(0, ...grid.map((reel) => reel.length))
  return Array.from({ length: rowCount }, (_, row) =>
    grid.map((reel) => reel[row] ?? ''),
  )
}

/** "1 × 9 lines": the bet per line and the lines played. */
export function stakeText({ betPerLine, lines }: Stake, t: TFunction) {
  return t('roundsPage.stake.text', {
    betPerLine: formatCredits(betPerLine),
    count: lines,
  })
}

/** "9 (1 × 9 lines)": the total stake with its bet per line and lines. */
export function stakeWithTotalText(
  { stakeTotal, stake }: Pick<RoundDetail, 'stakeTotal' | 'stake'>,
  t: TFunction,
) {
  return t('roundsPage.stake.withTotal', {
    total: formatCredits(stakeTotal),
    stake: stakeText(stake, t),
  })
}

/** "3 of 10": a feature's games played of its total. */
export function progressText(
  { gamesPlayed, gamesTotal }: { gamesPlayed: number; gamesTotal: number },
  t: TFunction,
) {
  return t('roundsPage.progress', {
    played: formatNumber(gamesPlayed),
    total: formatNumber(gamesTotal),
  })
}

/**
 * "Free game 3 of 10" from the feature's progress (retriggers included in
 * the total), else "Free game n" by its place among the round's free games.
 */
export function freeGameLabel(spin: SpinPlay, index: number, t: TFunction) {
  if (spin.feature === null) {
    return t('roundsPage.freeGame.numbered', {
      number: formatNumber(index + 1),
    })
  }
  const { gamesPlayed, gamesTotal } = spin.feature.state
  return t('roundsPage.freeGame.progress', {
    played: formatNumber(gamesPlayed),
    total: formatNumber(gamesTotal),
  })
}

/**
 * The expanding symbol of a Book of Ra feature, picked at the trigger;
 * undefined for no feature, another kind of feature or data that does not
 * decode.
 */
export function expandingSymbolOf(feature: FeatureUpdate | null) {
  if (feature?.featureKind !== FEATURE_KIND_EXPANDING) {
    return undefined
  }
  try {
    return decodeExpandingSymbolData(feature.state.data).expandingSymbolId
  } catch {
    return undefined
  }
}

/**
 * The win multiplier of a multiplier feature, fixed at the trigger;
 * undefined for no feature, another kind of feature or data that does not
 * decode.
 */
export function winMultiplierOf(feature: FeatureUpdate | null) {
  if (feature?.featureKind !== FEATURE_KIND_MULTIPLIER) {
    return undefined
  }
  try {
    return decodeMultiplierFeatureData(feature.state.data).winMultiplier
  } catch {
    return undefined
  }
}

/**
 * What a feature's trigger decided, as a label and its value for the
 * round page's facts, by kind: "Expanding symbol" (Book of Ra) or "Win
 * multiplier" ("x3"). Undefined for no feature, an unknown kind or data
 * that does not decode.
 */
export function featureFactOf(
  feature: FeatureUpdate | null,
  t: TFunction,
): [label: string, value: string] | undefined {
  const symbol = expandingSymbolOf(feature)
  if (symbol !== undefined) {
    return [t('roundsPage.feature.expandingSymbol'), symbol]
  }
  const multiplier = winMultiplierOf(feature)
  return multiplier === undefined
    ? undefined
    : [
        t('roundsPage.feature.winMultiplier'),
        t('roundsPage.feature.multiplierValue', { multiplier }),
      ]
}
